import { GetReportsByFilterRequest, InterventionReport } from '@inplay/models';
import { useOrganizationContext } from '../service/context/organizationContext';
import { useResponsiveLayout } from './useResponsiveLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GetInterventionReportsResponse,
  InterventionReportsFilter,
  InterventionReportsKebabOption,
  InterventionReportsPendingAction,
} from '../pages/interventionReports/interventionReports.type';
import { validateInterventionReportsForDeletion } from '../pages/interventionReports/interventionReportsHelper';
import { ReportService } from '../service/reportService';
import { toastV2 } from '../component/toastV2/toastV2';
import { useTranslation } from 'react-i18next';
import { useUserDetailContext } from '../service/context/userDetailContext';
import { SelectedUser } from '../component/userVerificationModal/userVerificationModal.types';

export const useInterventionReports = () => {
  const { t } = useTranslation();

  const { selectedVenueBranch, loading: organizationContextLoading } =
    useOrganizationContext();
  const { userDetail } = useUserDetailContext();
  const { isTablet } = useResponsiveLayout();
  const [selectedInterventionReport, setSelectedInterventionReport] =
    useState<InterventionReport | null>(null);
  const [filters, setFilters] = useState<InterventionReportsFilter>({
    searchQuery: '',
    selectedDate: new Date(),
  });
  const [InterventionReports, setInterventionReports] = useState<
    InterventionReport[]
  >([]);
  const [openCreateIntervention, setOpenCreateIntervention] =
    useState<boolean>(false);
  const [isReviewAndEditMode, setIsReviewAndEditMode] = useState(false);

  const [loading, setLoading] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<InterventionReport[]>([]);
  const [interventionReportForCreate, setInterventionReportForCreate] =
    useState<InterventionReport | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showUserVerification, setShowUserVerification] =
    useState<boolean>(false);
  const [verifiedUser, setVerifiedUser] = useState<SelectedUser | null>(null);
  const [showSelectPlayerModal, setShowSelectPlayerModal] =
    useState<boolean>(false);

  const handleOnVerify = (user: SelectedUser) => {
    setVerifiedUser(user);
    setShowUserVerification(false);

    switch (pendingAction) {
      case InterventionReportsPendingAction.CREATE_INTERVENTION_REPORT:
        setOpenCreateIntervention(true);
        break;
      case InterventionReportsPendingAction.DELETE_REPORTS:
        setShowDeleteConfirmation(true);
        setPendingAction(null);
        break;
      case InterventionReportsPendingAction.EDIT_REPORT:
        setOpenCreateIntervention(true);
        setPendingAction(null);
        break;
      default:
        break;
    }
  };

  const handleOnProceed = () => {
    setShowSelectPlayerModal(false);
    switch (pendingAction) {
      case InterventionReportsPendingAction.CREATE_INTERVENTION_REPORT:
        setOpenCreateIntervention(true);
        setPendingAction(null);
        break;
      default:
        break;
    }
  };

  const reportService = useMemo(() => new ReportService(), []);

  const handleOnChangeSearch = (query: string) => {
    setFilters((previousFilter) => ({ ...previousFilter, searchQuery: query }));
  };

  const handleReportInterventionClick = () => {
    setPendingAction(
      InterventionReportsPendingAction.CREATE_INTERVENTION_REPORT,
    );
    setShowUserVerification(true);
  };

  const handleReportInterventionClose = () => {
    setOpenCreateIntervention(false);
    setVerifiedUser(null);
    setInterventionReportForCreate(null);
  };

  const handleClearFilters = () => { };

  const fetchInterventionReports = useCallback(
    async (override?: Partial<InterventionReportsFilter>) => {
      try {
        console.log(
          '[useInterventionReports][fetchInterventionReports] entry',
          { override },
        );
        if (!selectedVenueBranch) return;
        console.log('[useInterventionReports][fetchInterventionReports] Entry');
        const filtersToUse = { ...filters, ...override };
        setLoading(true);
        const query: GetReportsByFilterRequest = {
          venueBranchId: selectedVenueBranch._id,
          currentDate: filtersToUse.selectedDate,
        };
        console.log(
          '[useInterventionReports][fetchInterventionReports] query',
          query,
        );
        const response: GetInterventionReportsResponse =
          await reportService.getInterventionReports(query);
        const interventionReports: InterventionReport[] =
          response.data?.reports ?? [];
        setInterventionReports(interventionReports);
      } catch (error) {
        console.error('Error fetching intervention reports:', error);
        toastV2.error(t('interventionReports.toast.fetchError'));
      } finally {
        setLoading(false);
      }
    },
    [selectedVenueBranch],
  );

  const handleDateApply = (date: Date) => {
    console.log('[useInterventionReports][handleDateApply] entry', { date });
    const normalizedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    console.log('[useInterventionReports][handleDateApply]', {
      original: date,
      normalized: normalizedDate,
    });
    setFilters((prev) => ({ ...prev, selectedDate: normalizedDate }));
    fetchInterventionReports({ selectedDate: normalizedDate });
  };

  const toggleFilterModal = () => setOpenFilterModal((prev) => !prev);

  const handleApplyFilters = (
    newFilters: Partial<InterventionReportsFilter>,
  ) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    fetchInterventionReports(newFilters);
  };

  const handleSelectionChange = (rows: InterventionReport[]) => {
    setSelectedRows(rows);
  };

  const handleRowClick = (row: InterventionReport) => {
    setSelectedInterventionReport(row);
  };

  const handleKebabActionFromKebabMenu = async (
    action: InterventionReportsKebabOption,
    row: InterventionReport,
    rowIndex: number,
  ): Promise<void> => {
    console.log(
      '[useInterventionReports][handleKebabActionFromKebabMenu] ENTRY',
      {
        action,
        row,
        rowIndex,
      },
    );

    // TODO: Add each functionality here
    switch (action) {
      case InterventionReportsKebabOption.VIEW:
        setSelectedInterventionReport(row);
        return;
      case InterventionReportsKebabOption.EDIT:
        setInterventionReportForCreate(row);
        setPendingAction(InterventionReportsPendingAction.EDIT_REPORT);
        setShowUserVerification(true);
        setIsReviewAndEditMode(false);
        return;
      case InterventionReportsKebabOption.REVIEW_AND_SIGN:
        setInterventionReportForCreate(row);
        setOpenCreateIntervention(true);
        setIsReviewAndEditMode(true);
        return;
      case InterventionReportsKebabOption.DOWNLOAD:
      case InterventionReportsKebabOption.HISTORY:
        toastV2.warning(t('interventionReports.toast.featureNotAvailable'));
        return;
      case InterventionReportsKebabOption.DELETE:
        setSelectedRows([row]);
        setPendingAction(InterventionReportsPendingAction.DELETE_REPORTS);
        setShowUserVerification(true);
        return;
      default: {
        console.error('Invalid Intervention Report Kebab Option');
        const _exhaustiveCheck: never = action;
        return _exhaustiveCheck;
      }
    }
  };

  const handleReviewAndSignClick = () => {
    if (!selectedInterventionReport) return;
    setInterventionReportForCreate(selectedInterventionReport);
    setOpenCreateIntervention(true);
    setIsReviewAndEditMode(true);
    setSelectedInterventionReport(null);
  };
  const closeInterventionReportDetails = () => {
    setSelectedInterventionReport(null);
  };

  useEffect(() => {
    fetchInterventionReports();
  }, [selectedVenueBranch]);

  const handleDeleteClick = () => {
    setPendingAction(InterventionReportsPendingAction.DELETE_REPORTS);
    setShowUserVerification(true);
  };

  const handleDeleteInterventionReports = async () => {
    try {
      if (!selectedRows.length) return;

      const { valid, invalid } = validateInterventionReportsForDeletion(
        selectedRows,
        userDetail,
      );

      if (invalid.length > 0) {
        toastV2.error(invalid[0]);
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      if (!valid.length) {
        toastV2.error(t('interventionReports.toast.noValidReports'));
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      const originalReportIds = valid
        .map((row) => row.originalReportId)
        .filter((id): id is string => Boolean(id));

      if (!originalReportIds.length) {
        toastV2.error(t('interventionReports.toast.noValidIds'));
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      setDeleting(true);
      const response =
        await reportService.deleteInterventionReports(originalReportIds);
      const count = response.deletedInterventionReportsCount;

      toastV2.success(
        t('interventionReports.toast.deleteSuccess', {
          report:
            count === 1
              ? t('interventionReports.singleReport')
              : t('interventionReports.multipleReports'),
        }),
      );

      setSelectedRows([]);
      setShowDeleteConfirmation(false);
      await fetchInterventionReports(filters);
    } catch (error) {
      console.log('selectedRows', { selectedRows });
      console.error('Error deleting intervention reports:', error);
      toastV2.error(
        t('interventionReports.toast.deleteError', {
          report:
            selectedRows.length === 1
              ? t('interventionReports.singleReport')
              : t('interventionReports.multipleReports'),
        }),
      );
    } finally {
      setDeleting(false);
    }
  };

  return {
    selectedVenueBranch,
    isTablet,
    organizationContextLoading,
    fetchInterventionReports,
    handleClearFilters,
    handleReportInterventionClose,
    handleReportInterventionClick,
    handleKebabActionFromKebabMenu,
    handleOnChangeSearch,
    handleDateApply,
    loading,
    selectedInterventionReport,
    InterventionReports,
    filters,
    openFilterModal,
    toggleFilterModal,
    handleApplyFilters,
    openCreateIntervention,
    isReviewAndEditMode,
    selectedRows,
    handleSelectionChange,
    handleRowClick,
    interventionReportForCreate,
    t,
    handleReviewAndSignClick,
    closeInterventionReportDetails,
    setSelectedRows,
    handleDeleteClick,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    handleDeleteInterventionReports,
    deleting,
    showUserVerification,
    setShowUserVerification,
    handleOnVerify,
    handleOnProceed,
    setShowSelectPlayerModal,
    showSelectPlayerModal,
    verifiedUser,
  };
};
